import {
  type AdImpressionProperties,
  type AdRequestSentProperties,
  type AdResponseReceivedProperties,
  type AdSurface,
  type AdTelemetryEventName,
  type AdViewContextProperties,
} from '@/types/ad-telemetry'

export type GptTelemetryEvent =
  | {
      event: 'ad_request_sent'
      properties: AdRequestSentProperties
    }
  | {
      event: 'ad_response_received'
      properties: AdResponseReceivedProperties
    }
  | {
      event: 'ad_impression'
      properties: AdImpressionProperties
    }

type GptAdTelemetryOptions = {
  adSurface: Exclude<AdSurface, 'vast-overlay'>
  adUnitPath: string
  capture: (event: GptTelemetryEvent) => void
  getViewContext: () => AdViewContextProperties
  isEventEnabled?: (event: AdTelemetryEventName) => boolean
  createRequestId?: () => string
  now?: () => number
}

type ActiveRequest = {
  adRequestId: string
  requestStartedAt: number
  requestAttemptCount: number
  responseReceived: boolean
  isRefresh: boolean
  refreshSequence: number
  completed: boolean
  impressionSent: boolean
  onloadReceived: boolean
}

const formatRenderedSize = (size: googletag.events.SlotRenderEndedEvent['size']) =>
  Array.isArray(size) ? size.join('x') : size

export const createGptAdTelemetry = ({
  adSurface,
  adUnitPath,
  capture,
  getViewContext,
  isEventEnabled = () => true,
  createRequestId = () => crypto.randomUUID(),
  now = Date.now,
}: GptAdTelemetryOptions) => {
  let activeRequest: ActiveRequest | null = null
  let completedRequest: ActiveRequest | null = null
  let nextRefreshSequence = 0

  const emit = (event: GptTelemetryEvent) => {
    if (isEventEnabled(event.event)) capture(event)
  }

  // Only refresh requests are bound to viewability; a first request can race the observer's first report.
  const getRequestViewContext = (isRefresh: boolean): AdViewContextProperties => {
    const viewContext = getViewContext()

    if (isRefresh) return viewContext

    return {
      is_page_visible: viewContext.is_page_visible,
      screen_orientation: viewContext.screen_orientation,
    }
  }

  const sendImpression = (request: ActiveRequest) => {
    request.impressionSent = true

    emit({
      event: 'ad_impression',
      properties: {
        ad_surface: adSurface,
        ad_request_id: request.adRequestId,
        ad_unit_path: adUnitPath,
        time_to_impression_ms: now() - request.requestStartedAt,
        is_refresh: request.isRefresh,
        refresh_sequence: request.refreshSequence,
        ...getViewContext(),
      },
    })
  }

  const handleSlotRequested = () => {
    if (activeRequest !== null && !activeRequest.completed) {
      activeRequest.requestAttemptCount += 1
      return
    }

    const request = {
      adRequestId: createRequestId(),
      requestStartedAt: now(),
      requestAttemptCount: 1,
      responseReceived: false,
      isRefresh: completedRequest !== null,
      refreshSequence: nextRefreshSequence++,
      completed: false,
      impressionSent: false,
      onloadReceived: false,
    }

    activeRequest = request

    emit({
      event: 'ad_request_sent',
      properties: {
        ad_surface: adSurface,
        ad_request_id: request.adRequestId,
        ad_unit_path: adUnitPath,
        is_refresh: request.isRefresh,
        refresh_sequence: request.refreshSequence,
        ...getRequestViewContext(request.isRefresh),
      },
    })
  }

  const handleSlotResponseReceived = () => {
    if (activeRequest !== null && !activeRequest.completed) {
      activeRequest.responseReceived = true
    }
  }

  const handleSlotRenderEnded = (event: googletag.events.SlotRenderEndedEvent) => {
    if (activeRequest === null || activeRequest.completed) return

    activeRequest.completed = true
    completedRequest = activeRequest

    emit({
      event: 'ad_response_received',
      properties: {
        ad_surface: adSurface,
        ad_request_id: activeRequest.adRequestId,
        ad_unit_path: adUnitPath,
        is_empty: event.isEmpty,
        is_blocked: !activeRequest.responseReceived,
        response_time_ms: now() - activeRequest.requestStartedAt,
        request_attempt_count: activeRequest.requestAttemptCount,
        line_item_id: event.lineItemId,
        creative_id: event.creativeId,
        size: formatRenderedSize(event.size),
        is_refresh: activeRequest.isRefresh,
        refresh_sequence: activeRequest.refreshSequence,
      },
    })

    if (!event.isEmpty && completedRequest.onloadReceived) sendImpression(completedRequest)
  }

  const handleSlotOnload = () => {
    if (completedRequest !== null && !completedRequest.impressionSent) {
      sendImpression(completedRequest)
      return
    }

    if (activeRequest !== null && !activeRequest.completed) activeRequest.onloadReceived = true
  }

  return {
    handleSlotRequested,
    handleSlotResponseReceived,
    handleSlotRenderEnded,
    handleSlotOnload,
  }
}
